import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BridgeClubLayout from '../../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import { useBridgeClubAuth } from '../../../contexts/BridgeClubAuthContext';
import './FarmChallengeOptIn.css';

const FarmChallengeOptIn: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useBridgeClubAuth();
  const navigate = useNavigate();
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/bridge-club/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const handleOptIn = async () => {
    try {
      setEnrolling(true);
      setError(null);
      await bridgeClubAPI.enrollInFarmChallenge();
      navigate('/bridge-club/farm-challenge', { replace: true });
    } catch (err: any) {
      console.error('Failed to opt in:', err);
      setError('Failed to join. Please try again.');
      setEnrolling(false);
    }
  };

  const handleMaybeLater = () => {
    navigate('/bridge-club/dashboard', { replace: true });
  };

  if (authLoading) {
    return (
      <BridgeClubLayout>
        <div className="farm-challenge-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </BridgeClubLayout>
    );
  }

  return (
    <BridgeClubLayout>
      <div className="opt-in-page">
        <div className="opt-in-header">
          <span className="opt-in-icon">🏆</span>
          <h1>Welcome to Farm Challenge!</h1>
        </div>

        <div className="opt-in-content">
          {/* Section 1: What It Is */}
          <section className="opt-in-section">
            <h2>🌾 What It Is</h2>
            <p>
              Farm Challenge rewards you for tracking your farm activities. Log weights,
              eggs, milk, and harvests to earn points you can redeem for feed credit at
              your home store.
            </p>
          </section>

          {/* Section 2: How Points Work */}
          <section className="opt-in-section">
            <h2>💰 How Points Work</h2>
            <div className="points-breakdown">
              <div className="points-item">
                <span className="points-icon">🥚</span>
                <div className="points-details">
                  <strong>Egg logs</strong>
                  <span>5 pts base (up to 17 pts with consistency)</span>
                </div>
              </div>
              <div className="points-item">
                <span className="points-icon">🥛</span>
                <div className="points-details">
                  <strong>Milk logs</strong>
                  <span>5 pts base (up to 17 pts with consistency)</span>
                </div>
              </div>
              <div className="points-item">
                <span className="points-icon">⚖️</span>
                <div className="points-details">
                  <strong>Weight logs</strong>
                  <span>2-10 pts depending on animal size</span>
                </div>
              </div>
              <div className="points-item">
                <span className="points-icon">🔪</span>
                <div className="points-details">
                  <strong>Harvest logs</strong>
                  <span>10-100 pts depending on animal size</span>
                </div>
              </div>
              <div className="points-item highlight">
                <span className="points-icon">🔥</span>
                <div className="points-details">
                  <strong>Streak bonuses</strong>
                  <span>2x-4x multiplier for consecutive daily logging</span>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Logging Rules */}
          <section className="opt-in-section">
            <h2>📋 Logging Rules</h2>
            <ul className="rules-list">
              <li><strong>Eggs:</strong> 1 log per day (account-wide)</li>
              <li><strong>Milk:</strong> 1 log per day (account-wide)</li>
              <li><strong>Weights (large animals):</strong> 1 per animal per week</li>
              <li><strong>Weights (small animals):</strong> 5 per group per week, max 10 pts</li>
              <li><strong>Harvests:</strong> 500 pts max per animal category per year</li>
            </ul>
          </section>

          {/* Section 4: Streak Bonuses */}
          <section className="opt-in-section">
            <h2>🔥 Streak Bonuses</h2>
            <p className="section-intro">Log consistently to unlock multipliers!</p>
            <div className="streak-tiers">
              <div className="streak-tier">
                <span className="streak-days">7 days in a row</span>
                <span className="streak-bonus">2x points for the next week</span>
              </div>
              <div className="streak-tier">
                <span className="streak-days">14 days in a row</span>
                <span className="streak-bonus">3x points for the next week</span>
              </div>
              <div className="streak-tier highlight">
                <span className="streak-days">21 days in a row</span>
                <span className="streak-bonus">4x points for the next week</span>
              </div>
            </div>
            <p className="streak-note">
              Multipliers reset after the bonus week ends, but your streak counter continues!
            </p>
          </section>

          {/* Section 5: Consistency Rewards */}
          <section className="opt-in-section">
            <h2>📈 Consistency Rewards</h2>
            <p className="section-intro">
              The more consistent you are, the more you earn permanently!
            </p>
            <ul className="consistency-list">
              <li>Your base rate for egg/milk logs starts at <strong>5 points</strong></li>
              <li>Complete a perfect month (log every day for 4 weeks) and your base rate increases by <strong>1 point - permanently</strong></li>
              <li>After 12 perfect months, you'll earn <strong>17 points</strong> per log instead of 5!</li>
              <li>Miss a month? No penalty - you just don't advance. Keep your current rate.</li>
            </ul>
          </section>

          {/* Section 6: Why Track With Us? */}
          <section className="opt-in-section">
            <h2>🤝 Why Track With Us?</h2>
            <p>
              Your data helps us make better feed. When you share how your animals
              perform on Red Bridge feed, we learn what works best. Better data =
              better feed = healthier animals for everyone.
            </p>
            <p>
              All data is anonymized - we never share your name, farm, or location.
              We use aggregate performance data to improve our products.
            </p>
            <p className="data-note">
              Please log accurate information. Quality data helps us help you.
            </p>
          </section>

          {/* Section 7: Redemption */}
          <section className="opt-in-section">
            <h2>🎁 Redemption</h2>
            <div className="redemption-info">
              <div className="redemption-rate">
                <span className="rate-points">1 point</span>
                <span className="rate-equals">=</span>
                <span className="rate-value">$0.01</span>
              </div>
              <p>Redeem at your home store. No cash value, no transfers.</p>
            </div>
          </section>
        </div>

        {error && (
          <div className="opt-in-error">
            <p>{error}</p>
          </div>
        )}

        <div className="opt-in-actions">
          <button
            className="btn-opt-in"
            onClick={handleOptIn}
            disabled={enrolling}
          >
            {enrolling ? 'Joining...' : 'I Understand & Want to Participate'}
          </button>
          <button
            className="btn-maybe-later"
            onClick={handleMaybeLater}
            disabled={enrolling}
          >
            Maybe Later
          </button>
        </div>
      </div>
    </BridgeClubLayout>
  );
};

export default FarmChallengeOptIn;
